/**
 * The web app's block type registry (the 6 base ones). Single source of
 * truth for rendering/grading: the player and the local re-grading use the
 * SAME instance. Adding a type = registering its plugin here (the engine
 * doesn't change) — `learning-blocks`'s extensibility invariant.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { defaultRegistry } from "@space/shared";

export const blockRegistry = defaultRegistry();

/** UI metadata for each block type (for the authoring editor).
 * `label`/`hint` are MessageDescriptor: consumers render with `t()`. */
export interface BlockKindMeta {
  kind: string;
  label: MessageDescriptor;
  hint: MessageDescriptor;
}

export const BLOCK_KINDS: BlockKindMeta[] = [
  { kind: "quiz", label: msg`Quiz`, hint: msg`Single or multiple choice` },
  { kind: "flashcard", label: msg`Flashcard`, hint: msg`Front / back` },
  { kind: "fill-blank", label: msg`Fill in the blanks`, hint: msg`Fill the gaps` },
  { kind: "match", label: msg`Match`, hint: msg`Match the pairs` },
  { kind: "code", label: msg`Code`, hint: msg`Write or complete code` },
  { kind: "listen-type", label: msg`Listen and type`, hint: msg`Transcribe the audio` },
  // MEDIA-F3F: bloque `video` (VOD)
  { kind: "video", label: msg`Video`, hint: msg`Play a video (VOD)` },
];

/** Default `def` template for a new type (authoring). */
export function defaultDefFor(kind: string): unknown {
  switch (kind) {
    case "quiz": {
      return {
        prompt: "",
        options: [
          { id: "a", text: "" },
          { id: "b", text: "" },
        ],
        correct: ["a"],
        multiple: false,
      };
    }
    case "flashcard": {
      return { front: "", back: "" };
    }
    case "fill-blank": {
      return { text: "", blanks: [{ id: "1", answers: [""] }] };
    }
    case "match": {
      return {
        pairs: [
          { left: "", right: "" },
          { left: "", right: "" },
        ],
      };
    }
    case "code": {
      return { prompt: "", language: "text", checks: [{ kind: "contains", value: "" }] };
    }
    case "listen-type": {
      return { audioRef: "", transcript: "" };
    }
    // MEDIA-F3F: bloque `video`
    case "video": {
      return { videoAssetId: "", minWatchPct: 0.9 };
    }
    default: {
      return {};
    }
  }
}
